import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { supabase } from '../../api/supabase';
import { useAuth } from '../../context/AuthContext';
import { usePasscode } from '../../components/common/PasscodeModal';
import { getInvoicePaidAmount, getOpeningBalance, getOpeningBalancePaid } from '../../api/receiptAllocations';
import { ACCOUNTS } from '../../api/accounts';
import {
  TODAY, daysOverdue, ageBucketOf, monthKeyOf, monthLabelOf, quarterOfMonth, lastNMonthKeys, pctChange,
  oldestUnsettledDate, pctColor, ProgressBar, DonutGauge, AgingBar, TrendLineChart, QuarterlyBarChart,
} from './performanceViz';

// Run a Supabase query paged past the 1000-row cap. `buildQuery()` must return a fully
// formed query (with its own .select()); .range() is applied here.
async function fetchAllRows(buildQuery) {
  const PAGE = 1000;
  let all = [], from = 0;
  while (true) {
    const { data, error } = await buildQuery().range(from, from + PAGE - 1);
    if (error || !data || !data.length) break;
    all = all.concat(data);
    if (data.length < PAGE) break;
    from += PAGE;
  }
  return all;
}

// PostgREST/URL length caps how many values .in() can carry before the whole request 400s.
// A large agent (TLS-01 has 1,300+ clients) sails well past that, so the query silently
// fails and every downstream figure comes back zero. Split the id list into chunks, run one
// paginated fetch per chunk, and merge.
async function fetchAllInChunks(table, selectCols, inColumn, ids, applyFilters) {
  const CHUNK = 120;
  const PAGE = 1000;
  let all = [];
  for (let i = 0; i < ids.length; i += CHUNK) {
    const slice = ids.slice(i, i + CHUNK);
    let from = 0;
    while (true) {
      let q = supabase.from(table).select(selectCols).in(inColumn, slice);
      if (applyFilters) q = applyFilters(q);
      const { data, error } = await q.range(from, from + PAGE - 1);
      if (error || !data || !data.length) break;
      all = all.concat(data);
      if (data.length < PAGE) break;
      from += PAGE;
    }
  }
  return all;
}

export default function AgentDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { canSee } = useAuth();
  const [agent, setAgent] = useState(null);
  const [tab, setTab] = useState('clients');
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const { prompt: promptPasscode, PasscodeModal } = usePasscode();

  // Client ledger state
  const [clientRows, setClientRows] = useState([]);
  const [clientLoading, setClientLoading] = useState(false);

  // Per-client invoice drill-down — expanded inline under the client's row rather than
  // navigating away, so staff can see which of a client's invoices are actually driving
  // their balance without losing their place in this agent's list. Loaded lazily (only
  // once, on first expand) and cached per client_id so re-collapsing/re-expanding is free.
  const [expandedClientId, setExpandedClientId] = useState(null);
  const [clientInvoices, setClientInvoices] = useState({}); // client_id -> [{...}]
  const [clientInvoicesLoading, setClientInvoicesLoading] = useState(null); // client_id currently loading

  const loadClientInvoices = useCallback(async (clientId) => {
    if (clientInvoices[clientId]) return; // already cached
    setClientInvoicesLoading(clientId);
    try {
      const { data: invs } = await supabase.from('invoices')
        .select('id, invoice_number, issued_date, due_date, status, line_items')
        .eq('client_id', clientId)
        .neq('status', 'VOID')
        .order('issued_date', { ascending: false });

      const rows = await Promise.all((invs || []).map(async inv => {
        const total = (inv.line_items || []).reduce((s, li) => s + (parseFloat(li.unit_price ?? li.unitPrice ?? li.amount ?? 0) * parseFloat(li.qty ?? li.quantity ?? 1)), 0);
        const paid = await getInvoicePaidAmount(inv.id);
        const balance = total - paid;
        // Same "due date, falling back to issued date" convention as the rest of the app —
        // due_date is optional and often left blank.
        const dueOn = inv.due_date || inv.issued_date;
        const overdueDays = balance > 0.01 ? Math.max(0, daysOverdue(dueOn)) : 0;
        const payStatus = balance <= 0.01 ? 'Paid' : paid > 0.01 ? 'Partially Paid' : 'Unpaid';
        return { ...inv, isOpeningBalance: false, total, paid, balance, overdueDays, payStatus };
      }));

      // A client's AR balance can carry an Opening Balance too — a lump sum brought
      // forward (often from a client merge/correction) with no invoice row behind it at
      // all, so it would otherwise vanish from this drill-down while still counting
      // toward the balance shown one level up. Surface it as its own row, using the same
      // two helpers the rest of the app already uses for this exact figure.
      const [obTotal, obPaid, obLine] = await Promise.all([
        getOpeningBalance(clientId),
        getOpeningBalancePaid(clientId),
        supabase.from('journal_lines')
          .select('journal_entries!inner(entry_date, description, status)')
          .eq('party_type', 'client').eq('party_id', clientId)
          .eq('journal_entries.status', 'POSTED')
          .ilike('journal_entries.description', 'Opening balance%')
          .limit(1).maybeSingle(),
      ]);

      if (obTotal > 0.01) {
        const obDate = obLine?.data?.journal_entries?.entry_date || null;
        const obBalance = obTotal - obPaid;
        const overdueDays = obBalance > 0.01 ? Math.max(0, daysOverdue(obDate)) : 0;
        const payStatus = obBalance <= 0.01 ? 'Paid' : obPaid > 0.01 ? 'Partially Paid' : 'Unpaid';
        rows.push({
          id: `opening-balance-${clientId}`, invoice_number: 'Opening Balance',
          issued_date: obDate, isOpeningBalance: true,
          total: obTotal, paid: obPaid, balance: obBalance, overdueDays, payStatus,
        });
      }

      rows.sort((a, b) => (b.issued_date || '').localeCompare(a.issued_date || ''));
      setClientInvoices(prev => ({ ...prev, [clientId]: rows }));
    } finally {
      setClientInvoicesLoading(null);
    }
  }, [clientInvoices]);

  const toggleClientExpand = (clientId) => {
    const next = expandedClientId === clientId ? null : clientId;
    setExpandedClientId(next);
    if (next) loadClientInvoices(next);
  };

  // Personal ledger (commissions)
  const [commissions, setCommissions] = useState([]);
  const [commLoading, setCommLoading] = useState(false);

  // Quotations sent by this agent (sale_quotations), and how far each one progressed
  // down the Quotation → Pro Forma → Invoice pipeline.
  const [quotations, setQuotations] = useState([]);
  const [quotLoading, setQuotLoading] = useState(false);

  useEffect(() => {
    supabase.from('agents').select('*').eq('id', id).single().then(({ data }) => {
      setAgent(data);
      setLoading(false);
    });
  }, [id]);

  const loadClients = useCallback(async () => {
    setClientLoading(true);

    // 1. All clients linked to this agent by FK (includes zero-balance clients).
    // Paged — a big agent (TLS-01) has more than the 1000-row cap.
    const cls = await fetchAllRows(() => supabase
      .from('clients')
      .select('id, full_name, client_code, is_active')
      .eq('care_of_agent_id', id)
      .order('full_name'));

    if (!cls || cls.length === 0) { setClientRows([]); setClientLoading(false); return; }

    // Seed map with every client at zero
    const clientMap = {};
    cls.forEach(c => { clientMap[c.id] = { client_id: c.id, full_name: c.full_name, client_code: c.client_code, is_active: c.is_active, invoiced: 0, received: 0 }; });

    // 2. Overlay AR balances from the general ledger (same source as AccountsReceivable).
    // Scoped to the AR account (1030) specifically — without this filter, every party=client
    // line gets summed regardless of account, which silently blends in Client Deposits (2010,
    // a liability with the OPPOSITE normal balance) and understates the real AR balance by
    // however much sits in client deposits. Confirmed live: Head Office showed 21.7M here vs
    // the true AR-only figure of 32.2M computed the same way on the Performance tab.
    // Chunked because a big agent (TLS-01 = 1,300+ clients) overflows .in()'s URL limit.
    const clientIds = cls.map(c => c.id);
    const lines = await fetchAllInChunks(
      'journal_lines',
      'base_debit, base_credit, party_id, journal_entries!inner(status, is_reversed, reversal_of)',
      'party_id', clientIds,
      q => q.eq('account_id', ACCOUNTS.AR).eq('party_type', 'client').eq('journal_entries.status', 'POSTED'),
    );
    lines
      .filter(l => !l.journal_entries?.is_reversed && !l.journal_entries?.reversal_of)
      .forEach(l => {
        if (!clientMap[l.party_id]) return;
        clientMap[l.party_id].invoiced += Number(l.base_debit || 0);
        clientMap[l.party_id].received += Number(l.base_credit || 0);
      });

    const rows = Object.values(clientMap).map(r => ({ ...r, balance: r.invoiced - r.received }))
      // Deactivated clients with a zero balance (e.g. reversed opening-balance placeholders)
      // are noise here — their history stays in the journals.
      .filter(r => r.is_active !== false || Math.abs(r.balance) >= 0.005);
    rows.sort((a, b) => b.balance - a.balance || a.full_name.localeCompare(b.full_name));
    setClientRows(rows);
    setClientLoading(false);
  }, [id]);

  const loadCommissions = useCallback(async () => {
    setCommLoading(true);
    const { data } = await supabase
      .from('commissions')
      .select('*, tickets(ticket_ref, airline), visas(visa_ref), bookings(booking_ref, packages(package_name))')
      .eq('agent_ref_id', id)
      .order('created_at', { ascending: false });
    setCommissions(data || []);
    setCommLoading(false);
  }, [id]);

  const loadQuotations = useCallback(async () => {
    setQuotLoading(true);
    const { data } = await supabase
      .from('sale_quotations')
      .select('*, pro_forma:converted_pro_forma_id(quotation_number, converted_invoice_id)')
      .eq('agent_ref_id', id)
      .order('created_at', { ascending: false });
    setQuotations(data || []);
    setQuotLoading(false);
  }, [id]);

  // Performance tab: month-on-month trend, this-year-vs-last-year, and collections
  // (DSO / aging / overdue) for this one agent — all derived from the same AR ledger and
  // quotation data already used elsewhere on this page, just re-cut by time period instead
  // of collapsed into a single all-time or single-period number.
  const [perfLoading, setPerfLoading] = useState(false);
  const [perf, setPerf] = useState(null);

  const loadPerformance = useCallback(async () => {
    setPerfLoading(true);
    try {
      const cls = await fetchAllRows(() => supabase.from('clients').select('id, full_name, client_code').eq('care_of_agent_id', id));
      const clientIds = (cls || []).map(c => c.id);
      const clientNameOf = {};
      (cls || []).forEach(c => { clientNameOf[c.id] = c.full_name; });

      if (!clientIds.length) { setPerf({ empty: true }); setPerfLoading(false); return; }

      const [arLines, invRows] = await Promise.all([
        fetchAllInChunks(
          'journal_lines',
          'base_debit, base_credit, party_id, journal_entries!inner(entry_date, status, is_reversed, reversal_of)',
          'party_id', clientIds,
          q => q.eq('account_id', ACCOUNTS.AR).eq('party_type', 'client').eq('journal_entries.status', 'POSTED'),
        ),
        fetchAllInChunks('invoices', 'id, client_id, issued_date', 'client_id', clientIds, q => q.neq('status', 'VOID')),
      ]);

      // A reversed entry and its reversal net to zero — drop both so gross Sales/Collection
      // aren't inflated by a mistake that was corrected (same convention as Client Ledger).
      const arRows = arLines
        .filter(l => !l.journal_entries?.is_reversed && !l.journal_entries?.reversal_of)
        .map(l => ({ date: l.journal_entries.entry_date, debit: Number(l.base_debit || 0), credit: Number(l.base_credit || 0), party_id: l.party_id }));

      // ---- Monthly trend (trailing 12 months) ----
      const monthKeys = lastNMonthKeys(12);
      const monthly = monthKeys.map(k => ({ key: k, label: monthLabelOf(k), quotations: 0, invoices: 0, sales: 0, collection: 0 }));
      const monthIndex = {}; monthly.forEach((m, i) => { monthIndex[m.key] = i; });
      quotations.forEach(q => { const k = monthKeyOf(q.created_at); if (monthIndex[k] !== undefined) monthly[monthIndex[k]].quotations += 1; });
      invRows.forEach(inv => { const k = monthKeyOf(inv.issued_date); if (monthIndex[k] !== undefined) monthly[monthIndex[k]].invoices += 1; });
      arRows.forEach(l => { const k = monthKeyOf(l.date); if (monthIndex[k] !== undefined) { monthly[monthIndex[k]].sales += l.debit; monthly[monthIndex[k]].collection += l.credit; } });

      // Cumulative (since-inception) collection % as of each month-end — smooths out the
      // "collected an old invoice this month with no new sales" noise a period-flow % would
      // show, so it actually reads as a trend in collection discipline over time.
      const sortedAr = [...arRows].sort((a, b) => a.date.localeCompare(b.date));
      let cumDebit = 0, cumCredit = 0, arPtr = 0;
      monthly.forEach(m => {
        const boundary = `${m.key}-31`; // lexical compare on 'YYYY-MM-DD' is safe for a month-end cutoff
        while (arPtr < sortedAr.length && sortedAr[arPtr].date <= boundary) {
          cumDebit += sortedAr[arPtr].debit; cumCredit += sortedAr[arPtr].credit; arPtr++;
        }
        m.cumCollectionPct = cumDebit > 0 ? (cumCredit / cumDebit) * 100 : null;
      });

      // ---- Quarterly (current year) ----
      const thisYear = TODAY.getFullYear();
      const quarterly = [1, 2, 3, 4].map(q => ({ q, quotations: 0, invoices: 0, sales: 0, collection: 0 }));
      quotations.forEach(qt => {
        const d = new Date(qt.created_at);
        if (d.getFullYear() === thisYear) quarterly[quarterOfMonth(d.getMonth() + 1) - 1].quotations += 1;
      });
      invRows.forEach(inv => {
        const d = new Date(inv.issued_date);
        if (d.getFullYear() === thisYear) quarterly[quarterOfMonth(d.getMonth() + 1) - 1].invoices += 1;
      });
      arRows.forEach(l => {
        const d = new Date(l.date);
        if (d.getFullYear() === thisYear) { const qq = quarterly[quarterOfMonth(d.getMonth() + 1) - 1]; qq.sales += l.debit; qq.collection += l.credit; }
      });

      // ---- YTD vs prior-YTD (same day-of-year cutoff, so it's a fair comparison) ----
      const jan1This = `${thisYear}-01-01`;
      const jan1Last = `${thisYear - 1}-01-01`;
      const cutoffLast = `${thisYear - 1}-${String(TODAY.getMonth() + 1).padStart(2, '0')}-${String(TODAY.getDate()).padStart(2, '0')}`;
      const ytd = { quotations: 0, invoices: 0, sales: 0, collection: 0 };
      const priorYtd = { quotations: 0, invoices: 0, sales: 0, collection: 0 };
      quotations.forEach(q => {
        const d = q.created_at.slice(0, 10);
        if (d >= jan1This) ytd.quotations += 1;
        else if (d >= jan1Last && d <= cutoffLast) priorYtd.quotations += 1;
      });
      invRows.forEach(inv => {
        const d = inv.issued_date;
        if (d >= jan1This) ytd.invoices += 1;
        else if (d >= jan1Last && d <= cutoffLast) priorYtd.invoices += 1;
      });
      arRows.forEach(l => {
        const d = l.date;
        if (d >= jan1This) { ytd.sales += l.debit; ytd.collection += l.credit; }
        else if (d >= jan1Last && d <= cutoffLast) { priorYtd.sales += l.debit; priorYtd.collection += l.credit; }
      });

      // ---- Collections: DSO, aging, overdue clients ----
      const perClient = {};
      arRows.forEach(l => {
        if (!perClient[l.party_id]) perClient[l.party_id] = { debit: 0, credit: 0, lines: [] };
        const c = perClient[l.party_id];
        c.debit += l.debit; c.credit += l.credit;
        c.lines.push(l);
      });
      const currentBalance = Object.values(perClient).reduce((s, c) => s + (c.debit - c.credit), 0);
      const ninetyDaysAgo = new Date(TODAY.getTime() - 90 * 86400000).toISOString().slice(0, 10);
      const sales90 = arRows.filter(l => l.date >= ninetyDaysAgo).reduce((s, l) => s + l.debit, 0);
      const dso = sales90 > 0 ? (currentBalance / sales90) * 90 : null;

      const aging = { b0_30: 0, b31_60: 0, b61_90: 0, b90plus: 0 };
      const overdueClients = [];
      Object.entries(perClient).forEach(([clientId, c]) => {
        const balance = c.debit - c.credit;
        if (balance <= 0.01) return;
        const asOf = oldestUnsettledDate(c.lines) || TODAY.toISOString().slice(0, 10);
        const days = daysOverdue(asOf);
        const bucket = ageBucketOf(days);
        if (bucket === '0-30') aging.b0_30 += balance;
        else if (bucket === '31-60') aging.b31_60 += balance;
        else if (bucket === '61-90') aging.b61_90 += balance;
        else aging.b90plus += balance;
        if (bucket !== '0-30') overdueClients.push({ client_id: clientId, name: clientNameOf[clientId] || '—', balance, days });
      });
      overdueClients.sort((a, b) => b.balance - a.balance);

      setPerf({
        empty: false, monthly, quarterly, ytd, priorYtd, currentBalance, dso,
        aging, overdueClients, overdueCount: overdueClients.length,
        overdueTotal: overdueClients.reduce((s, c) => s + c.balance, 0),
      });
    } finally {
      setPerfLoading(false);
    }
  }, [id, quotations]);

  // Only deletable when nothing actually references it — an agent that's already been used
  // as a client's care-of agent, or has commission/ticket/visa/booking history, must stay
  // (deactivate instead) so those records don't lose their agent link.
  const deleteAgent = async () => {
    setDeleteError('');
    setDeleting(true);
    try {
      const counts = await Promise.all([
        supabase.from('clients').select('id', { count: 'exact', head: true }).eq('care_of_agent_id', id),
        supabase.from('commissions').select('id', { count: 'exact', head: true }).eq('agent_ref_id', id),
        supabase.from('tickets').select('id', { count: 'exact', head: true }).eq('care_of_agent_id', id),
        supabase.from('visas').select('id', { count: 'exact', head: true }).eq('care_of_agent_id', id),
        supabase.from('bookings').select('id', { count: 'exact', head: true }).eq('care_of_agent_id', id),
        supabase.from('sale_quotations').select('id', { count: 'exact', head: true }).eq('agent_ref_id', id),
      ]);
      const total = counts.reduce((s, r) => s + (r.count || 0), 0);
      if (total > 0) {
        setDeleteError(`Cannot delete — this agent has ${total} linked record(s) (clients, commissions, tickets, visas, or bookings). Mark it Inactive instead.`);
        setDeleting(false);
        return;
      }
      try { await promptPasscode(); } catch { setDeleting(false); return; }
      const { error } = await supabase.from('agents').delete().eq('id', id);
      if (error) throw error;
      navigate('/agents');
    } catch (err) {
      setDeleteError(err.message);
      setDeleting(false);
    }
  };

  useEffect(() => {
    if (!loading) {
      if (tab === 'clients') loadClients();
      if (tab === 'commissions') loadCommissions();
      if (tab === 'performance') loadPerformance();
    }
  }, [tab, loading, loadClients, loadCommissions, loadPerformance]);

  // Quotations load unconditionally (not just when that tab is active) since the
  // conversion-rate stat cards up top need them regardless of which tab is showing.
  useEffect(() => { if (!loading) loadQuotations(); }, [loading, loadQuotations]);

  if (loading) return <div className="loading-screen"><div className="spinner" /></div>;
  if (!agent) return <div className="page-header"><div className="page-title">Agent not found</div></div>;

  const totalBalance = clientRows.reduce((s, r) => s + r.balance, 0);
  const totalComm = commissions.reduce((s, c) => s + parseFloat(c.commission_amount || 0), 0);
  const paidComm = commissions.filter(c => c.commission_status === 'PAID').reduce((s, c) => s + parseFloat(c.commission_amount || 0), 0);
  const convertedToInvoice = quotations.filter(q => q.pro_forma?.converted_invoice_id).length;
  const conversionRate = quotations.length ? (convertedToInvoice / quotations.length * 100) : 0;

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{ fontFamily: 'monospace', fontSize: 15, background: 'var(--primary)', color: '#fff', padding: '4px 12px', borderRadius: 6 }}>{agent.tls_code}</span>
            {agent.full_name}
          </div>
          <div className="page-subtitle">{agent.mobile || agent.phone || ''}{agent.email ? '  ·  ' + agent.email : ''}</div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {canSee('agent_sales') && <button className="btn btn-secondary btn-sm" onClick={() => navigate(`/reports/agent-sales?agent=${id}`)}>Sales report</button>}
          <button className="btn btn-secondary btn-sm" onClick={() => navigate(`/agents/${id}/edit`)}>Edit</button>
          <button className="btn btn-danger btn-sm" disabled={deleting} onClick={deleteAgent}>{deleting ? 'Deleting…' : 'Delete'}</button>
        </div>
      </div>

      {deleteError && <div style={{ color: 'var(--danger)', marginBottom: 14, padding: '10px 14px', background: 'rgba(239,68,68,0.06)', borderRadius: 7 }}>{deleteError}</div>}
      {PasscodeModal}

      {/* Stat cards */}
      <div className="stats-grid" style={{ marginBottom: 20 }}>
        <div className="stat-card"><div className="stat-label">Clients Under Care</div><div className="stat-value">{clientRows.length}</div></div>
        <div className="stat-card"><div className="stat-label">Total Client AR</div><div className="stat-value amount" style={{ color: 'var(--danger)' }}>{totalBalance.toLocaleString(undefined, { maximumFractionDigits: 0 })}</div></div>
        <div className="stat-card"><div className="stat-label">Total Commission</div><div className="stat-value amount">{totalComm.toLocaleString(undefined, { maximumFractionDigits: 0 })}</div></div>
        <div className="stat-card"><div className="stat-label">Commission Paid</div><div className="stat-value amount" style={{ color: 'var(--success)' }}>{paidComm.toLocaleString(undefined, { maximumFractionDigits: 0 })}</div></div>
        <div className="stat-card"><div className="stat-label">Quotations Sent</div><div className="stat-value">{quotations.length}</div></div>
        <div className="stat-card"><div className="stat-label">Converted to Invoice</div><div className="stat-value">{convertedToInvoice} <span style={{ fontSize: 13, fontWeight: 400, color: 'var(--text-muted)' }}>({conversionRate.toFixed(0)}%)</span></div></div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 16, borderBottom: '2px solid var(--border)', paddingBottom: 0 }}>
        {[['clients', 'Client Ledger'], ['performance', 'Total Performance'], ['commissions', 'Personal Ledger (Commissions)'], ['quotations', 'Quotations Sent']].map(([key, label]) => (
          <button key={key} onClick={() => setTab(key)} style={{ padding: '10px 20px', border: 'none', background: 'none', cursor: 'pointer', fontWeight: tab === key ? 700 : 400, color: tab === key ? 'var(--primary)' : 'var(--text-muted)', borderBottom: tab === key ? '2px solid var(--primary)' : '2px solid transparent', marginBottom: -2, fontSize: 14 }}>
            {label}
          </button>
        ))}
      </div>

      {/* Client Ledger Tab */}
      {tab === 'clients' && (
        clientLoading ? <div className="spinner" /> : (
          <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: 'var(--bg)', borderBottom: '2px solid var(--border)' }}>
                  <th style={{ padding: '10px 16px', textAlign: 'left', fontWeight: 600, fontSize: 12, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Client</th>
                  <th style={{ padding: '10px 16px', textAlign: 'right', fontWeight: 600, fontSize: 12, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Invoiced</th>
                  <th style={{ padding: '10px 16px', textAlign: 'right', fontWeight: 600, fontSize: 12, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Received</th>
                  <th style={{ padding: '10px 16px', textAlign: 'right', fontWeight: 600, fontSize: 12, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Balance</th>
                </tr>
              </thead>
              <tbody>
                {clientRows.map(r => {
                  const expanded = expandedClientId === r.client_id;
                  const invLoading = clientInvoicesLoading === r.client_id;
                  const invoices = clientInvoices[r.client_id];
                  return (
                    <React.Fragment key={r.client_id}>
                      <tr
                        onClick={() => toggleClientExpand(r.client_id)}
                        style={{ borderBottom: expanded ? 'none' : '1px solid var(--border)', cursor: 'pointer', background: expanded ? 'var(--bg)' : 'transparent' }}
                        onMouseEnter={e => { if (!expanded) e.currentTarget.style.background = 'var(--bg)'; }}
                        onMouseLeave={e => { if (!expanded) e.currentTarget.style.background = 'transparent'; }}
                      >
                        <td style={{ padding: '10px 16px' }}>
                          <span style={{ display: 'inline-block', width: 14, color: 'var(--text-muted)', fontSize: 11 }}>{expanded ? '▼' : '▶'}</span>
                          <span style={{ fontWeight: 600 }}>{r.full_name}</span>
                          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginLeft: 14 }}>{r.client_code}</div>
                        </td>
                        <td style={{ padding: '10px 16px', textAlign: 'right' }}>{r.invoiced.toLocaleString(undefined, { maximumFractionDigits: 0 })}</td>
                        <td style={{ padding: '10px 16px', textAlign: 'right', color: 'var(--success)' }}>{r.received.toLocaleString(undefined, { maximumFractionDigits: 0 })}</td>
                        <td style={{ padding: '10px 16px', textAlign: 'right', fontWeight: 700, color: r.balance > 0 ? 'var(--danger)' : 'var(--success)' }}>{r.balance.toLocaleString(undefined, { maximumFractionDigits: 0 })}</td>
                      </tr>
                      {expanded && (
                        <tr style={{ borderBottom: '1px solid var(--border)' }}>
                          <td colSpan={4} style={{ padding: '0 16px 14px 40px', background: 'var(--bg)' }}>
                            {invLoading ? (
                              <div style={{ padding: '10px 0', fontSize: 12, color: 'var(--text-muted)' }}>Loading invoices…</div>
                            ) : !invoices || invoices.length === 0 ? (
                              <div style={{ padding: '10px 0', fontSize: 12, color: 'var(--text-muted)', fontStyle: 'italic' }}>No invoices for this client.</div>
                            ) : (
                              <div style={{ border: '1px solid var(--border)', borderRadius: 6, overflow: 'hidden', background: 'var(--surface, white)' }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                                  <thead>
                                    <tr style={{ background: 'rgba(0,0,0,0.03)', borderBottom: '1px solid var(--border)' }}>
                                      <th style={{ padding: '7px 10px', textAlign: 'left', fontWeight: 600, color: 'var(--text-muted)' }}>Invoice</th>
                                      <th style={{ padding: '7px 10px', textAlign: 'left', fontWeight: 600, color: 'var(--text-muted)' }}>Issued</th>
                                      <th style={{ padding: '7px 10px', textAlign: 'right', fontWeight: 600, color: 'var(--text-muted)' }}>Total</th>
                                      <th style={{ padding: '7px 10px', textAlign: 'right', fontWeight: 600, color: 'var(--text-muted)' }}>Paid</th>
                                      <th style={{ padding: '7px 10px', textAlign: 'right', fontWeight: 600, color: 'var(--text-muted)' }}>Balance</th>
                                      <th style={{ padding: '7px 10px', textAlign: 'right', fontWeight: 600, color: 'var(--text-muted)' }}>Overdue</th>
                                      <th style={{ padding: '7px 10px', textAlign: 'center', fontWeight: 600, color: 'var(--text-muted)' }}>Status</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {invoices.map(inv => {
                                      const statusColor = inv.payStatus === 'Paid' ? { bg: '#dcfce7', fg: '#15803d' }
                                        : inv.payStatus === 'Partially Paid' ? { bg: '#fef9c3', fg: '#854d0e' }
                                        : { bg: '#fee2e2', fg: '#b91c1c' };
                                      return (
                                        <tr key={inv.id}
                                          onClick={e => { e.stopPropagation(); if (!inv.isOpeningBalance) navigate(`/invoices/${inv.id}`); }}
                                          style={{ borderBottom: '1px solid var(--border)', cursor: inv.isOpeningBalance ? 'default' : 'pointer', fontStyle: inv.isOpeningBalance ? 'italic' : 'normal' }}
                                          onMouseEnter={e => { if (!inv.isOpeningBalance) e.currentTarget.style.background = 'rgba(79,110,247,0.06)'; }}
                                          onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                                        >
                                          <td style={{ padding: '7px 10px', fontWeight: 600, color: inv.isOpeningBalance ? 'var(--text-muted)' : 'var(--primary)' }}>
                                            {inv.invoice_number}
                                            {inv.isOpeningBalance && <span title="Brought forward — no invoice record behind it" style={{ marginLeft: 4, cursor: 'help' }}>ⓘ</span>}
                                          </td>
                                          <td style={{ padding: '7px 10px', color: 'var(--text-muted)' }}>{inv.issued_date?.slice(0, 10) || '—'}</td>
                                          <td style={{ padding: '7px 10px', textAlign: 'right' }}>{inv.total.toLocaleString(undefined, { maximumFractionDigits: 0 })}</td>
                                          <td style={{ padding: '7px 10px', textAlign: 'right', color: 'var(--success)' }}>{inv.paid.toLocaleString(undefined, { maximumFractionDigits: 0 })}</td>
                                          <td style={{ padding: '7px 10px', textAlign: 'right', fontWeight: 700, color: inv.balance > 0.01 ? 'var(--danger)' : 'var(--success)' }}>{inv.balance.toLocaleString(undefined, { maximumFractionDigits: 0 })}</td>
                                          <td style={{ padding: '7px 10px', textAlign: 'right', color: inv.overdueDays > 90 ? 'var(--danger)' : inv.overdueDays > 30 ? '#d97706' : 'var(--text-muted)', fontWeight: inv.overdueDays > 30 ? 600 : 400 }}>
                                            {inv.balance > 0.01 ? `${inv.overdueDays}d` : '—'}
                                          </td>
                                          <td style={{ padding: '7px 10px', textAlign: 'center' }}>
                                            <span style={{ fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 99, background: statusColor.bg, color: statusColor.fg }}>{inv.payStatus}</span>
                                          </td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </table>
                              </div>
                            )}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
                {clientRows.length === 0 && <tr><td colSpan={4} style={{ padding: 30, textAlign: 'center', color: 'var(--text-muted)' }}>No clients under this agent yet</td></tr>}
              </tbody>
              {clientRows.length > 0 && (
                <tfoot>
                  <tr style={{ background: 'var(--bg)', borderTop: '2px solid var(--border)', fontWeight: 700 }}>
                    <td style={{ padding: '10px 16px' }}>TOTAL</td>
                    <td style={{ padding: '10px 16px', textAlign: 'right' }}>{clientRows.reduce((s, r) => s + r.invoiced, 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}</td>
                    <td style={{ padding: '10px 16px', textAlign: 'right', color: 'var(--success)' }}>{clientRows.reduce((s, r) => s + r.received, 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}</td>
                    <td style={{ padding: '10px 16px', textAlign: 'right', color: 'var(--danger)' }}>{totalBalance.toLocaleString(undefined, { maximumFractionDigits: 0 })}</td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        )
      )}

      {/* Total Performance Tab */}
      {tab === 'performance' && (
        perfLoading || !perf ? <div className="spinner" /> : perf.empty ? (
          <div className="card" style={{ padding: 30, textAlign: 'center', color: 'var(--text-muted)' }}>No clients under this agent yet</div>
        ) : (
          <div>
            {/* Sales vs Collection — trailing 12 months */}
            <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 10 }}>
              Sales vs Collection — Trailing 12 Months
            </div>
            <TrendLineChart monthly={perf.monthly} />

            {/* YTD vs Prior YTD */}
            <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 10 }}>
              {TODAY.getFullYear()} Year-to-Date vs {TODAY.getFullYear() - 1} (same period)
            </div>
            <div className="stats-grid" style={{ marginBottom: 24 }}>
              {[
                ['Quotations', perf.ytd.quotations, perf.priorYtd.quotations, false],
                ['Invoices', perf.ytd.invoices, perf.priorYtd.invoices, false],
                ['Sales', perf.ytd.sales, perf.priorYtd.sales, true],
                ['Collection', perf.ytd.collection, perf.priorYtd.collection, true],
              ].map(([label, curr, prev, isMoney]) => {
                const change = pctChange(curr, prev);
                return (
                  <div key={label} className="stat-card">
                    <div className="stat-label">{label} (YTD)</div>
                    <div className="stat-value">{isMoney ? curr.toLocaleString(undefined, { maximumFractionDigits: 0 }) : curr}</div>
                    <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
                      vs {isMoney ? prev.toLocaleString(undefined, { maximumFractionDigits: 0 }) : prev} last year
                      {change !== null && (
                        <span style={{ marginLeft: 6, fontWeight: 700, color: change >= 0 ? 'var(--success)' : 'var(--danger)' }}>
                          {change >= 0 ? '▲' : '▼'} {Math.abs(change).toFixed(0)}%
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Collections: gauge + DSO + overdue summary */}
            <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 10 }}>Collections</div>
            <div className="card" style={{ display: 'flex', alignItems: 'center', gap: 28, padding: '18px 24px', marginBottom: 16, flexWrap: 'wrap' }}>
              <DonutGauge
                pct={perf.monthly[perf.monthly.length - 1]?.cumCollectionPct ?? null}
                color={pctColor(perf.monthly[perf.monthly.length - 1]?.cumCollectionPct ?? null)}
                label="Collection Rate"
              />
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 16, flex: 1 }}>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Days Sales Outstanding</div>
                  <div style={{ fontSize: 22, fontWeight: 700 }}>{perf.dso === null ? '—' : perf.dso.toFixed(0)}{perf.dso !== null && <span style={{ fontSize: 13, fontWeight: 400, color: 'var(--text-muted)' }}> days</span>}</div>
                </div>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Overdue Clients</div>
                  <div style={{ fontSize: 22, fontWeight: 700, color: perf.overdueCount > 0 ? 'var(--danger)' : 'var(--success)' }}>{perf.overdueCount}</div>
                </div>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Overdue Amount</div>
                  <div style={{ fontSize: 22, fontWeight: 700, color: perf.overdueTotal > 0 ? 'var(--danger)' : 'var(--success)' }}>{perf.overdueTotal.toLocaleString(undefined, { maximumFractionDigits: 0 })}</div>
                </div>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Current Balance</div>
                  <div style={{ fontSize: 22, fontWeight: 700 }}>{perf.currentBalance.toLocaleString(undefined, { maximumFractionDigits: 0 })}</div>
                </div>
              </div>
            </div>

            {/* Aging breakdown — proportional bar, then the numbers behind it */}
            <AgingBar aging={perf.aging} />
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 8, marginBottom: 24 }}>
              {[
                { label: '0–30 Days (Current)', value: perf.aging.b0_30, color: '#16a34a', bg: '#f0fdf4', border: '#bbf7d0' },
                { label: '31–60 Days', value: perf.aging.b31_60, color: '#d97706', bg: '#fffbeb', border: '#fde68a' },
                { label: '61–90 Days', value: perf.aging.b61_90, color: '#ea580c', bg: '#fff7ed', border: '#fed7aa' },
                { label: '90+ Days (Overdue)', value: perf.aging.b90plus, color: '#dc2626', bg: '#fef2f2', border: '#fecaca' },
              ].map(c => (
                <div key={c.label} style={{ background: c.bg, border: `1px solid ${c.border}`, borderRadius: 8, padding: '12px 14px' }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: c.color, marginBottom: 4 }}>{c.label}</div>
                  <div style={{ fontSize: 17, fontWeight: 700, color: c.color }}>{c.value.toLocaleString(undefined, { maximumFractionDigits: 0 })}</div>
                </div>
              ))}
            </div>

            {/* Overdue clients — who to chase */}
            {perf.overdueClients.length > 0 && (
              <div style={{ marginBottom: 24 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 10 }}>Overdue Clients — Oldest Balance First</div>
                <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                    <thead>
                      <tr style={{ background: 'var(--bg)', borderBottom: '2px solid var(--border)' }}>
                        <th style={{ padding: '8px 16px', textAlign: 'left', fontWeight: 600, fontSize: 12, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Client</th>
                        <th style={{ padding: '8px 16px', textAlign: 'right', fontWeight: 600, fontSize: 12, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Balance</th>
                        <th style={{ padding: '8px 16px', textAlign: 'right', fontWeight: 600, fontSize: 12, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Days Overdue</th>
                      </tr>
                    </thead>
                    <tbody>
                      {perf.overdueClients.slice(0, 10).map(c => (
                        <tr key={c.client_id} style={{ borderBottom: '1px solid var(--border)', cursor: 'pointer' }} onClick={() => { setTab('clients'); toggleClientExpand(c.client_id); }}>
                          <td style={{ padding: '8px 16px' }}>{c.name}</td>
                          <td style={{ padding: '8px 16px', textAlign: 'right', fontWeight: 700, color: 'var(--danger)' }}>{c.balance.toLocaleString(undefined, { maximumFractionDigits: 0 })}</td>
                          <td style={{ padding: '8px 16px', textAlign: 'right', color: c.days > 90 ? 'var(--danger)' : '#d97706', fontWeight: 600 }}>{c.days}d</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {perf.overdueClients.length > 10 && <div style={{ padding: '8px 16px', fontSize: 12, color: 'var(--text-muted)' }}>+{perf.overdueClients.length - 10} more — see Client Ledger tab</div>}
                </div>
              </div>
            )}

            {/* Monthly trend */}
            <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 10 }}>Month-on-Month (Trailing 12 Months)</div>
            <div className="card" style={{ padding: 0, overflow: 'hidden', marginBottom: 24 }}>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ background: 'var(--bg)', borderBottom: '2px solid var(--border)' }}>
                      <th style={{ padding: '8px 16px', textAlign: 'left', fontWeight: 600, fontSize: 12, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Month</th>
                      <th style={{ padding: '8px 16px', textAlign: 'right', fontWeight: 600, fontSize: 12, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Quotations</th>
                      <th style={{ padding: '8px 16px', textAlign: 'right', fontWeight: 600, fontSize: 12, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Invoices</th>
                      <th style={{ padding: '8px 16px', textAlign: 'right', fontWeight: 600, fontSize: 12, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Sales</th>
                      <th style={{ padding: '8px 16px', textAlign: 'right', fontWeight: 600, fontSize: 12, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Collection</th>
                      <th style={{ padding: '8px 16px', textAlign: 'right', fontWeight: 600, fontSize: 12, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Cumulative Collection %</th>
                    </tr>
                  </thead>
                  <tbody>
                    {perf.monthly.map(m => (
                      <tr key={m.key} style={{ borderBottom: '1px solid var(--border)' }}>
                        <td style={{ padding: '8px 16px', fontWeight: 600 }}>{m.label}</td>
                        <td style={{ padding: '8px 16px', textAlign: 'right' }}>{m.quotations}</td>
                        <td style={{ padding: '8px 16px', textAlign: 'right' }}>{m.invoices}</td>
                        <td style={{ padding: '8px 16px', textAlign: 'right' }}>{m.sales.toLocaleString(undefined, { maximumFractionDigits: 0 })}</td>
                        <td style={{ padding: '8px 16px', textAlign: 'right', color: 'var(--success)' }}>{m.collection.toLocaleString(undefined, { maximumFractionDigits: 0 })}</td>
                        <td style={{ padding: '8px 16px' }}>
                          {m.cumCollectionPct === null ? (
                            <span style={{ color: 'var(--text-muted)' }}>—</span>
                          ) : (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <div style={{ flex: 1, minWidth: 60 }}><ProgressBar pct={m.cumCollectionPct} color={pctColor(m.cumCollectionPct)} /></div>
                              <span style={{ width: 38, textAlign: 'right', fontWeight: 700, color: pctColor(m.cumCollectionPct) }}>{m.cumCollectionPct.toFixed(0)}%</span>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 24 }}>
              "Cumulative Collection %" is total collected ÷ total invoiced since this agent's clients' very first transaction, as of the end of that month — it shows the trend of the all-time collection rate, not that single month's flow (a period-flow % swings wildly whenever an old invoice gets settled in a slow month for new sales).
            </div>

            {/* Quarterly breakdown */}
            <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 10 }}>{TODAY.getFullYear()} by Quarter</div>
            <div style={{ marginBottom: 16 }}><QuarterlyBarChart quarterly={perf.quarterly} /></div>
            <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: 'var(--bg)', borderBottom: '2px solid var(--border)' }}>
                    <th style={{ padding: '8px 16px', textAlign: 'left', fontWeight: 600, fontSize: 12, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Quarter</th>
                    <th style={{ padding: '8px 16px', textAlign: 'right', fontWeight: 600, fontSize: 12, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Quotations</th>
                    <th style={{ padding: '8px 16px', textAlign: 'right', fontWeight: 600, fontSize: 12, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Invoices</th>
                    <th style={{ padding: '8px 16px', textAlign: 'right', fontWeight: 600, fontSize: 12, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Sales</th>
                    <th style={{ padding: '8px 16px', textAlign: 'right', fontWeight: 600, fontSize: 12, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Collection</th>
                  </tr>
                </thead>
                <tbody>
                  {perf.quarterly.map(q => {
                    const isFuture = q.q > quarterOfMonth(TODAY.getMonth() + 1);
                    return (
                      <tr key={q.q} style={{ borderBottom: '1px solid var(--border)', opacity: isFuture ? 0.4 : 1 }}>
                        <td style={{ padding: '8px 16px', fontWeight: 600 }}>Q{q.q}</td>
                        <td style={{ padding: '8px 16px', textAlign: 'right' }}>{q.quotations}</td>
                        <td style={{ padding: '8px 16px', textAlign: 'right' }}>{q.invoices}</td>
                        <td style={{ padding: '8px 16px', textAlign: 'right' }}>{q.sales.toLocaleString(undefined, { maximumFractionDigits: 0 })}</td>
                        <td style={{ padding: '8px 16px', textAlign: 'right', color: 'var(--success)' }}>{q.collection.toLocaleString(undefined, { maximumFractionDigits: 0 })}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )
      )}

      {/* Personal Ledger (Commissions) Tab */}
      {tab === 'commissions' && (
        commLoading ? <div className="spinner" /> : (
          <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: 'var(--bg)', borderBottom: '2px solid var(--border)' }}>
                  <th style={{ padding: '10px 16px', textAlign: 'left', fontWeight: 600, fontSize: 12, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Reference</th>
                  <th style={{ padding: '10px 16px', textAlign: 'left', fontWeight: 600, fontSize: 12, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Type</th>
                  <th style={{ padding: '10px 16px', textAlign: 'right', fontWeight: 600, fontSize: 12, color: 'var(--text-muted)', textTransform: 'uppercase' }}>GP</th>
                  <th style={{ padding: '10px 16px', textAlign: 'right', fontWeight: 600, fontSize: 12, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Commission</th>
                  <th style={{ padding: '10px 16px', textAlign: 'center', fontWeight: 600, fontSize: 12, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {commissions.map(c => {
                  const ref = c.tickets?.ticket_ref || c.visas?.visa_ref || c.bookings?.booking_ref || '—';
                  const desc = c.tickets?.airline || c.visas ? 'Visa' : c.bookings?.packages?.package_name || '';
                  return (
                    <tr key={c.id} style={{ borderBottom: '1px solid var(--border)' }}>
                      <td style={{ padding: '10px 16px' }}>
                        <div style={{ fontWeight: 600, color: 'var(--primary)' }}>{ref}</div>
                        <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{desc}</div>
                      </td>
                      <td style={{ padding: '10px 16px', color: 'var(--text-muted)' }}>{c.product_type}</td>
                      <td style={{ padding: '10px 16px', textAlign: 'right' }}>{parseFloat(c.gp_amount || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}</td>
                      <td style={{ padding: '10px 16px', textAlign: 'right', fontWeight: 700, color: 'var(--primary)' }}>{parseFloat(c.commission_amount || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}</td>
                      <td style={{ padding: '10px 16px', textAlign: 'center' }}>
                        <span style={{ fontSize: 11, fontWeight: 600, padding: '3px 10px', borderRadius: 99, background: c.commission_status === 'PAID' ? '#dcfce7' : '#fef9c3', color: c.commission_status === 'PAID' ? '#16a34a' : '#854d0e' }}>
                          {c.commission_status}
                        </span>
                      </td>
                    </tr>
                  );
                })}
                {commissions.length === 0 && <tr><td colSpan={5} style={{ padding: 30, textAlign: 'center', color: 'var(--text-muted)' }}>No commissions recorded yet</td></tr>}
              </tbody>
              {commissions.length > 0 && (
                <tfoot>
                  <tr style={{ background: 'var(--bg)', borderTop: '2px solid var(--border)', fontWeight: 700 }}>
                    <td colSpan={3} style={{ padding: '10px 16px' }}>TOTAL</td>
                    <td style={{ padding: '10px 16px', textAlign: 'right', color: 'var(--primary)' }}>{totalComm.toLocaleString(undefined, { maximumFractionDigits: 0 })}</td>
                    <td />
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        )
      )}

      {/* Quotations Sent Tab */}
      {tab === 'quotations' && (
        quotLoading ? <div className="spinner" /> : (
          <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: 'var(--bg)', borderBottom: '2px solid var(--border)' }}>
                  <th style={{ padding: '10px 16px', textAlign: 'left', fontWeight: 600, fontSize: 12, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Quotation #</th>
                  <th style={{ padding: '10px 16px', textAlign: 'left', fontWeight: 600, fontSize: 12, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Title / Destination</th>
                  <th style={{ padding: '10px 16px', textAlign: 'center', fontWeight: 600, fontSize: 12, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Status</th>
                  <th style={{ padding: '10px 16px', textAlign: 'center', fontWeight: 600, fontSize: 12, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Pro Forma</th>
                  <th style={{ padding: '10px 16px', textAlign: 'center', fontWeight: 600, fontSize: 12, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Invoiced</th>
                </tr>
              </thead>
              <tbody>
                {quotations.map(q => (
                  <tr key={q.id} style={{ borderBottom: '1px solid var(--border)', cursor: 'pointer' }} onClick={() => navigate(`/sale-quotations/${q.id}`)}>
                    <td style={{ padding: '10px 16px', fontWeight: 600, color: 'var(--primary)' }}>{q.quotation_number}</td>
                    <td style={{ padding: '10px 16px' }}>{[q.title, q.destination].filter(Boolean).join(' — ') || '—'}</td>
                    <td style={{ padding: '10px 16px', textAlign: 'center' }}>
                      <span style={{ fontSize: 11, fontWeight: 600, padding: '3px 10px', borderRadius: 99, background: q.status === 'SENT' ? '#dcfce7' : '#f3f4f6', color: q.status === 'SENT' ? '#16a34a' : '#6b7280' }}>
                        {q.status}
                      </span>
                    </td>
                    <td style={{ padding: '10px 16px', textAlign: 'center' }}>
                      {q.pro_forma
                        ? <span style={{ color: 'var(--primary)', fontWeight: 600 }} onClick={e => { e.stopPropagation(); navigate(`/quotations/${q.converted_pro_forma_id}`); }}>{q.pro_forma.quotation_number}</span>
                        : <span style={{ color: 'var(--text-muted)' }}>—</span>}
                    </td>
                    <td style={{ padding: '10px 16px', textAlign: 'center' }}>
                      {q.pro_forma?.converted_invoice_id
                        ? <span style={{ color: 'var(--success)', fontWeight: 600 }} onClick={e => { e.stopPropagation(); navigate(`/invoices/${q.pro_forma.converted_invoice_id}`); }}>Yes →</span>
                        : <span style={{ color: 'var(--text-muted)' }}>—</span>}
                    </td>
                  </tr>
                ))}
                {quotations.length === 0 && <tr><td colSpan={5} style={{ padding: 30, textAlign: 'center', color: 'var(--text-muted)' }}>No quotations sent yet</td></tr>}
              </tbody>
            </table>
          </div>
        )
      )}
    </div>
  );
}
